import { VCPM } from '../models/project.model';

export type WorkloadClusterKind = 'HCI' | 'COMPUTE' | 'STORAGE';
export type WorkloadClusterStatus = 'new' | 'update' | 'pending';

export const CLUSTER_NAME_PREFIX: Record<WorkloadClusterKind, string> = {
  HCI: 'HCICluster',
  COMPUTE: 'ComputeCluster',
  STORAGE: 'StorageCluster',
};

/** Name the store's Create*Cluster actions mint for the given counter (absent counter reads as 1). */
export function buildNewClusterName(kind: WorkloadClusterKind, nextNumber: number | undefined): string {
  return `${CLUSTER_NAME_PREFIX[kind]}-${nextNumber ?? 1}`;
}

const USE_EXISTING_CLUSTER_MODE = 'use-existing-cluster';
const EMPTY_CLUSTER_REFERENCE = { id: null, name: null } as unknown as VCPM.ClusterReference;

/**
 * Returns a copy of a cloned workload whose cluster references are reset per side: a side whose
 * hardware preference is 'use-existing-cluster' keeps its reference (the clone keeps showing the
 * cluster it already points at, no new cluster), every other side is cleared so the normal Add
 * Workload pipeline (collectPendingClusterCreates) creates a brand new cluster for the clone and
 * bumps the naming counter. dHCI sides are independent: a mixed workload only loses the reference
 * of its owning side.
 */
export function prepareClonedWorkloadClusterReferences(workload: VCPM.Workload): VCPM.Workload {
  const clone: VCPM.Workload = { ...workload };
  const isDhci = workload.vcfDeploymentInputs?.deploymentType === 'dHCI';
  const computeMode = (isDhci ? workload.computeClusterHardwareConfiguration : workload.hardwareConfiguration)?.hardwareConfigMode;
  if (computeMode !== USE_EXISTING_CLUSTER_MODE && workload.computeCluster) {
    clone.computeCluster = { ...EMPTY_CLUSTER_REFERENCE };
  }
  if (isDhci && workload.storageClusterHardwareConfiguration?.hardwareConfigMode !== USE_EXISTING_CLUSTER_MODE && workload.storageCluster) {
    clone.storageCluster = { ...EMPTY_CLUSTER_REFERENCE };
  }
  return clone;
}

/**
 * Makes a freshly deep-copied (already re-id'd) workload domain self-contained after a domain clone.
 * Mutates `domain` and `data` in place (the caller owns both copies):
 * - every domain-local HCI/Compute entity is re-minted (new id + next counter name) and every
 *   workload reference to it is remapped, so the clone keeps its own internal sharing;
 * - every project-wide Storage cluster owned by a workload of this domain (a non-'use-existing-cluster'
 *   storage side) gets a new StorageCluster entity for the clone and the domain's references to it
 *   are remapped; storage sides that use an existing cluster keep pointing at it.
 */
export function remapClonedDomainClusters(domain: VCPM.WorkloadDomain, data: VCPM.ProjectData): void {
  const remap = new Map<string, VCPM.ClusterReference>();
  const bump = (kind: WorkloadClusterKind): string => {
    const field = kind === 'HCI' ? 'nextHciClusterNumber' : kind === 'COMPUTE' ? 'nextComputeClusterNumber' : 'nextStorageClusterNumber';
    const n = data[field] ?? 1;
    data[field] = n + 1;
    return buildNewClusterName(kind, n);
  };

  domain.clusterConfigurations = (domain.clusterConfigurations ?? []).map((entity) => {
    const minted = { ...entity, id: crypto.randomUUID(), name: bump(entity.type === 'HCI' ? 'HCI' : 'COMPUTE') } as VCPM.ReusableComputeCluster;
    remap.set(entity.id, { id: minted.id, name: minted.name });
    return minted;
  });

  const storageById = new Map((data.clusterConfigurations ?? []).map((c) => [c.id, c]));
  const storageRemap = new Map<string, VCPM.ClusterReference>();
  (domain.workloadList ?? []).forEach((workload) => {
    const ref = workload.storageCluster;
    const owns = workload.vcfDeploymentInputs?.deploymentType === 'dHCI'
      && workload.storageClusterHardwareConfiguration?.hardwareConfigMode !== USE_EXISTING_CLUSTER_MODE;
    const source = ref?.id ? storageById.get(ref.id) : undefined;
    if (owns && source && !storageRemap.has(source.id)) {
      const minted = { ...source, id: crypto.randomUUID(), name: bump('STORAGE'), workloadDomainId: domain.id as number } as VCPM.StorageCluster;
      data.clusterConfigurations = [...(data.clusterConfigurations ?? []), minted];
      storageRemap.set(source.id, { id: minted.id, name: minted.name });
    }
  });

  domain.workloadList = (domain.workloadList ?? []).map((workload) => {
    const clone: VCPM.Workload = { ...workload };
    const compute = workload.computeCluster?.id ? remap.get(workload.computeCluster.id) : undefined;
    const storage = workload.storageCluster?.id ? storageRemap.get(workload.storageCluster.id) : undefined;
    if (compute) {
      clone.computeCluster = { ...compute };
    }
    if (storage) {
      clone.storageCluster = { ...storage };
    }
    return clone;
  });
}

function nextNumberFor(project: VCPM.Project | null | undefined, kind: WorkloadClusterKind): number | undefined {
  const data = project?.data;
  switch (kind) {
    case 'HCI': return data?.nextHciClusterNumber;
    case 'COMPUTE': return data?.nextComputeClusterNumber;
    case 'STORAGE': return data?.nextStorageClusterNumber;
  }
}

function countWorkloadsReferencing(project: VCPM.Project | null | undefined, kind: WorkloadClusterKind, clusterId: string): number {
  let count = 0;
  for (const domain of project?.data?.workloadDomains ?? []) {
    for (const workload of domain.workloadList ?? []) {
      const ref = kind === 'STORAGE' ? workload.storageCluster : workload.computeCluster;
      if (ref?.id === clusterId) {
        count++;
      }
    }
  }
  return count;
}

/**
 * Name + badge status shown above a workload's cluster hardware block. Returns an empty pending name for an unpicked reuse selection; otherwise
 * with no saved reference it previews the name the store will mint on save (same prefix + counter).
 * 'update' = joining an existing cluster (picked via Use Existing Cluster, or a cluster other
 * workloads already reference); 'new' = a brand new cluster will be created with this workload.
 */
export function resolveWorkloadClusterLabel(
  project: VCPM.Project | null | undefined,
  kind: WorkloadClusterKind,
  ref: Partial<VCPM.ClusterReference> | null | undefined,
  hardwareConfigMode: string | null | undefined,
): { name: string; status: WorkloadClusterStatus } {
  if (hardwareConfigMode === USE_EXISTING_CLUSTER_MODE && (!ref?.id || !ref?.name)) {
    return { name: '', status: 'pending' };
  }
  if (ref?.id && ref?.name) {
    const joiningExisting =
      hardwareConfigMode === 'use-existing-cluster' || countWorkloadsReferencing(project, kind, ref.id) > 1;
    return { name: ref.name, status: joiningExisting ? 'update' : 'new' };
  }
  return { name: buildNewClusterName(kind, nextNumberFor(project, kind)), status: 'new' };
}
